// @flow
import { combineReducers } from "redux";
import * as actionTypes from "../constants/actionTypes";
import type { Action as ItemsAction } from "../actions/items";
import type { Action as LanguagesesAction } from "../actions/language";
import type { Entities } from "../types/entities";
import type { CombinedReducer } from "redux";

export type ItemsState = {
  +entities: Entities,
  +page: number,
  +itemsPerPage: number,
  +total: number,
  +isLoading: boolean,
  +hasError: boolean,
  +errorMessage: ?string,
  +selectedItem: ?Object,
  +editMode: boolean
};

type Action = LanguagesesAction | ItemsAction;

const isLoading = (state = false, action: Action) => {
  switch (action.type) {
    case actionTypes.SEARCH_ITEMS:
    case actionTypes.GET_NEXT_PAGE_FOR_ITEMS:
    case actionTypes.UNPUBLISH_ITEM:
    case actionTypes.REMOVE_TAG_FROM_ITEM:
      return true;

    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SEARCH_ITEMS_SUCCESS:
    case actionTypes.SEARCH_ITEMS_ERROR:
    case actionTypes.GET_ITEM_BY_ID_SUCCESS:
    case actionTypes.GET_ITEM_BY_ID_ERROR:
    case actionTypes.SELECT_LANGUAGE:
    case actionTypes.UNPUBLISH_ITEM_ERROR:
    case actionTypes.UNPUBLISH_ITEM_SUCCESS:
    case actionTypes.REMOVE_TAG_FROM_ITEM_ERROR:
    case actionTypes.REMOVE_TAG_FROM_ITEM_SUCCESS:
      return false;

    default:
      return state;
  }
};

const hasError = (state = false, action: Action) => {
  switch (action.type) {
    case actionTypes.SEARCH_ITEMS:
    case actionTypes.GET_NEXT_PAGE_FOR_ITEMS:
    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SEARCH_ITEMS_SUCCESS:
    case actionTypes.GET_ITEM_BY_ID_SUCCESS:
    case actionTypes.SELECT_LANGUAGE:
    case actionTypes.UNPUBLISH_ITEM:
    case actionTypes.UNPUBLISH_ITEM_SUCCESS:
    case actionTypes.REMOVE_TAG_FROM_ITEM:
    case actionTypes.REMOVE_TAG_FROM_ITEM_SUCCESS:
      return false;

    case actionTypes.SEARCH_ITEMS_ERROR:
    case actionTypes.GET_ITEM_BY_ID_ERROR:
    case actionTypes.UNPUBLISH_ITEM_ERROR:
    case actionTypes.REMOVE_TAG_FROM_ITEM_ERROR:
      return true;
    default:
      return state;
  }
};

const errorMessage = (state = null, action: Action) => {
  switch (action.type) {
    case actionTypes.SEARCH_ITEMS:
    case actionTypes.GET_NEXT_PAGE_FOR_ITEMS:
    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SEARCH_ITEMS_SUCCESS:
    case actionTypes.GET_ITEM_BY_ID_SUCCESS:
    case actionTypes.SELECT_LANGUAGE:
    case actionTypes.UNPUBLISH_ITEM:
    case actionTypes.UNPUBLISH_ITEM_SUCCESS:
    case actionTypes.REMOVE_TAG_FROM_ITEM:
    case actionTypes.REMOVE_TAG_FROM_ITEM_SUCCESS:
      return null;

    case actionTypes.SEARCH_ITEMS_ERROR:
    case actionTypes.GET_ITEM_BY_ID_ERROR:
    case actionTypes.UNPUBLISH_ITEM_ERROR:
    case actionTypes.REMOVE_TAG_FROM_ITEM_ERROR:
      const { error } = action.payload;
      return error;
    default:
      return state;
  }
};

const page = (state = 0, action: Action) => {
  switch (action.type) {
    case actionTypes.GET_NEXT_PAGE_FOR_ITEMS:
      const { page } = action.payload;
      return page;

    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SELECT_LANGUAGE:
      return 0;

    default:
      return state;
  }
};

const itemsPerPage = (state = 30, action: Action) => {
  switch (action.type) {
    default:
      return state;
  }
};

const total = (state = 0, action: Action) => {
  switch (action.type) {
    case actionTypes.SEARCH_ITEMS_SUCCESS:
      const { total } = action.payload;
      return total;

    case actionTypes.UNPUBLISH_ITEM_SUCCESS:
      return state - 1;

    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SELECT_LANGUAGE:
      return 0;

    default:
      return state;
  }
};

const entities = (
  state = {
    all: [],
    byId: {}
  },
  action: Action
): Entities => {
  switch (action.type) {
    case actionTypes.SEARCH_ITEMS_SUCCESS:
      const { entities, page } = action.payload;
      const newState =
        page === 0
          ? applySetResult(state, entities)
          : applyUpdateResult(state, entities);

      return newState;

    case actionTypes.UNPUBLISH_ITEM_SUCCESS:
      const { item } = action.payload;
      const newAll = state.all.filter(id => item.id !== id);
      const newById = { ...state.byId };
      delete newById[item.id];
      return {
        all: newAll,
        byId: newById
      };

    case actionTypes.REMOVE_TAG_FROM_ITEM_SUCCESS:
      const { tagID, itemID } = action.payload;
      const _newById = { ...state.byId };
      const tags = _newById[itemID].tags.filter(id => tagID !== id);
      _newById[itemID].tags = tags;
      return {
        all: state.all,
        byId: _newById
      };

    case actionTypes.RESET_PAGE_FOR_ITEMS:
    case actionTypes.SELECT_LANGUAGE:
      return {
        all: [],
        byId: {}
      };

    default:
      return state;
  }
};

const applySetResult = (state, entities) => {
  const { all, byId } = entities;
  return {
    all,
    byId
  };
};

const applyUpdateResult = (state, entities) => {
  const prevAllItems = state.all;
  const prevItemByIds = state.byId;
  const { all, byId } = entities;
  return {
    all: [...prevAllItems, ...all],
    byId: { ...prevItemByIds, ...byId }
  };
};

const selectedItem = (state = null, action: Action) => {
  switch (action.type) {
    case actionTypes.SELECT_ITEM:
    case actionTypes.GET_ITEM_BY_ID_SUCCESS:
      const { item } = action.payload;
      return item;

    case actionTypes.SELECT_LANGUAGE:
      return null;

    default:
      return state;
  }
};

const editMode = (state = false, action: Action) => {
  switch (action.type) {
    case actionTypes.TOGGLE_EDIT_MODE:
      return !state;
    default:
      return state;
  }
};

const items: CombinedReducer<ItemsState, Action> = combineReducers({
  entities,
  page,
  itemsPerPage,
  total,
  isLoading,
  hasError,
  errorMessage,
  selectedItem,
  editMode
});

export default items;



// WEBPACK FOOTER //
// ./src/reducers/items.js